import { CimConnection } from '../data/cim-connection';
import { FileTransfer } from '../data/file-transfer';
import { PowerShellConnection } from '../data/powershell-connection';
import { EnvironmentConnectionTypeInfo, EnvironmentModule } from '../manifest/environment-modules';
import { Connection, ConnectionUtility } from './connection';
import { ConnectionManager } from './connection-manager';

/**
 * Provides a shortcut to accessing common information about the active connection.
 */
export class ActiveConnection {
    /**
     * map data of connection type information.
     */
    private map: { [name: string]: EnvironmentConnectionTypeInfo } = null;

    /**
     * Gets the active connection
     */
    public get value(): Connection {
        return this.connectionManager.activeConnection;
    }

    /**
     * Sets the active connection
     */
    public set value(value: Connection) {
        this.connectionManager.activeConnection = value;
    }

    /**
     * Indicates if the active connection is to a server
     */
    public get isServer(): boolean {
        return ConnectionUtility.isServer(this.value);
    }

    /**
     * Indicates if the active connection is to a cluster
     */
    public get isCluster(): boolean {
        return ConnectionUtility.isCluster(this.value);
    }

    /**
     * Indicates if the active connection is to a node (cluster or server)
     */
    public get isNode(): boolean {
        return ConnectionUtility.isNode(this.value);
    }

    /**
     * Indicates if the active connection is to a failover cluster
     * @deprecated since 09/16/19. Please use isBritannicEnabled from ClusterInventoryCache instead
     */
    public get isFailoverCluster(): boolean {
        return ConnectionUtility.isFailoverCluster(this.value);
    }

    /**
     * Indicates if the active connection is to a windows client
     */
    public get isWindowsClient(): boolean {
        return ConnectionUtility.isWindowsClient(this.value);
    }

    /**
     * Indicates if the active connection is to an EFLOW device
     */
    public get isEflowDevice(): boolean {
        return ConnectionUtility.isEflowDevice(this.value);
    }

    /**
     * If the active connection is a cluster, the cluster node names. Otherwise, an empty array
     */
    public get clusterNodes(): string[] {
        return ConnectionUtility.getClusterNodes(this.value);
    }

    /**
     * If the active connection is a node (cluster or server), the node name. Otherwise, null.
     */
    public get nodeName(): string {
        return ConnectionUtility.getNodeName(this.value);
    }

    /**
     * If the active connection is a node (cluster or server), the valid node name (may be alias). Otherwise, null.
     */
    public get validNodeName(): string {
        return ConnectionUtility.getValidNodeName(this.value);
    }

    /**
     * Gets the connection type info for the active connection
     */
    public get connectionTypeInfo(): EnvironmentConnectionTypeInfo {
        if (!this.map) {
            // create once.
            this.map = EnvironmentModule.getConnectionMap();
        }

        return this.value ? this.map[this.value.type] : null;
    }

    /**
     * Constructor for the active connection class
     */
    constructor(
        private connectionManager: ConnectionManager,
        private cimConnection: CimConnection,
        private powerShellConnection: PowerShellConnection,
        private fileTransfer: FileTransfer
    ) {
    }
}
